import { cn } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import type { HouseFinancingListItem } from "@apps/dashboard/lib/home-expense-types";

export function FinancingTermProgressBlock({
  row,
  className,
}: {
  row: HouseFinancingListItem;
  className?: string;
}): JSX.Element {
  const { t, locale } = useI18n();

  if (row.totalInstallmentCount <= 0) {
    return (
      <p className={cn("text-label-sm text-on-surface-variant", className)}>
        {t("dashboardUi.financings.listNoInstallmentSchedule")}
      </p>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-label-sm text-on-surface-variant">
        <span>{t("dashboardUi.financings.listTermProgress", { percent: row.termElapsedPercent })}</span>
        {row.isContractEnded ? (
          <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-label-sm text-on-surface">
            {t("dashboardUi.financings.listContractEndedBadge")}
          </span>
        ) : null}
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-surface-container-high"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={row.termElapsedPercent}
        aria-valuetext={t("dashboardUi.financings.listTermProgress", {
          percent: row.termElapsedPercent,
        })}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width]",
            row.isContractEnded ? "bg-on-surface-variant/50" : "bg-primary",
          )}
          style={{
            width: `${Math.min(100, Math.max(0, row.termElapsedPercent))}%`,
          }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-label-sm text-on-surface-variant">
        <span>
          {t("dashboardUi.financings.listInstallmentsSummary", {
            past: row.pastInstallmentCount,
            remaining: row.remainingInstallmentCount,
            total: row.totalInstallmentCount,
          })}
        </span>
        <span>
          {t("dashboardUi.financings.listEstimatedRemaining", {
            amount: formatMinorAsCurrency(row.estimatedRemainingMinor, row.currency, locale),
          })}
        </span>
      </div>
    </div>
  );
}
